import { Seo } from "@/components/Seo";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { SERVICES } from "@/lib/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROPERTY_TYPE_VALUES = ["kuca", "stan", "pomocna_zgrada", "gospodarska_zgrada", "drugo"];

const empty = {
  full_name: "",
  email: "",
  phone: "",
  service_type: "",
  client_address: "",
  client_oib: "",
  property_address: "",
  city: "",
  property_type: "",
  year_built: "",
  area_m2: "",
  cadastral_municipality: "",
  cadastral_parcel: "",
  description: "",
  website: "",
};

export default function PrijavaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => {
    const usluga = searchParams.get("usluga");
    const vrsta = searchParams.get("vrsta");
    return {
      ...empty,
      service_type: SERVICES.some((s) => s.slug === usluga) ? usluga : "",
      property_type: PROPERTY_TYPE_VALUES.includes(vrsta) ? vrsta : "",
    };
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = t("prijava.errors.fullName");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("prijava.errors.email");
    if (form.phone.trim().length < 5) e.phone = t("prijava.errors.phone");
    if (!form.service_type) e.service_type = t("prijava.errors.service");
    if (form.client_oib.trim() && !/^\d{11}$/.test(form.client_oib.trim())) e.client_oib = t("prijava.errors.oib");
    if (form.property_address.trim().length < 2) e.property_address = t("prijava.errors.propertyAddress");
    if (form.city.trim().length < 2) e.city = t("prijava.errors.city");
    if (form.description.trim().length < 10) e.description = t("prijava.errors.description");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t("prijava.errors.checkRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success(t("prijava.successToast"));
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("prijava.errors.sendFailed");
      toast.error(typeof msg === "string" ? msg : t("prijava.errors.sendFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-28 text-center" data-testid="prijava-success">
        <CheckCircle2 className="w-14 h-14 text-ink mx-auto" strokeWidth={1.25} />
        <div className="overline text-ink-soft mt-6">{t("prijava.success.kicker")}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-ink mt-4 leading-tight">
          {t("prijava.success.title1")}<br />{t("prijava.success.title2")}
        </h1>
        <p className="text-ink-soft mt-6 leading-relaxed max-w-xl mx-auto">
          {t("prijava.success.text")}
        </p>
        <div className="mt-10 flex justify-center gap-3 flex-wrap">
          <button
            onClick={() => { setDone(false); }}
            className="border border-ink text-ink px-6 py-3 text-sm font-medium rounded-sm hover:bg-secondary transition-colors"
            data-testid="prijava-new-btn"
          >
            {t("prijava.success.newBtn")}
          </button>
          <button
            onClick={() => navigate("/")}
            className="bg-ink text-white px-6 py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors inline-flex items-center gap-2"
            data-testid="prijava-home-btn"
          >
            {t("prijava.success.homeBtn")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="prijava-page">
      <Seo title={t("prijava.seoTitle")} path="/prijava" description={t("prijava.seoDesc")} />
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> {t("prijava.kicker")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                {t("prijava.heroTitle1")}<br />{t("prijava.heroTitle2")}
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              {t("prijava.heroLead")}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="prijava-form">
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={set("website")}
            data-testid="hp-website"
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
          />
          {/* SIDE */}
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-24 border border-hair p-6 bg-white">
              <ShieldCheck className="w-5 h-5 text-ink" strokeWidth={1.5} />
              <div className="overline text-ink-soft mt-4">{t("prijava.privacyKicker")}</div>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                {t("prijava.privacyText")}
              </p>
              <div className="h-px bg-hair my-6" />
              <div className="overline text-ink-soft">{t("prijava.helpKicker")}</div>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                {t("prijava.helpText")}
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="md:col-span-8 space-y-10">
            <FieldSet title={t("prijava.section1")} n="01">
              <Field label={t("prijava.fields.service")} error={errors.service_type}>
                <Select value={form.service_type} onValueChange={set("service_type")}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="select-service">
                    <SelectValue placeholder={t("prijava.fields.servicePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.slug} data-testid={`option-service-${s.slug}`}>
                        {t(`services.items.${s.slug}.title`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </FieldSet>

            <FieldSet title={t("prijava.section2")} n="02">
              <Field label={t("prijava.fields.fullName")} error={errors.full_name}>
                <Input
                  data-testid="input-full-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder={t("prijava.fields.fullNamePlaceholder")}
                  className="rounded-sm"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("prijava.fields.email")} error={errors.email}>
                  <Input
                    type="email"
                    data-testid="input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t("prijava.fields.emailPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
                <Field label={t("prijava.fields.phone")} error={errors.phone}>
                  <Input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t("prijava.fields.phonePlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("prijava.fields.clientAddress")} error={errors.client_address}>
                  <Input
                    data-testid="input-client-address"
                    value={form.client_address}
                    onChange={set("client_address")}
                    placeholder={t("prijava.fields.clientAddressPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
                <Field label={t("prijava.fields.clientOib")} error={errors.client_oib}>
                  <Input
                    data-testid="input-client-oib"
                    value={form.client_oib}
                    onChange={set("client_oib")}
                    placeholder={t("prijava.fields.clientOibPlaceholder")}
                    inputMode="numeric"
                    maxLength={11}
                    className="rounded-sm"
                  />
                </Field>
              </div>
            </FieldSet>

            <FieldSet title={t("prijava.section3")} n="03">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("prijava.fields.propertyAddress")} error={errors.property_address}>
                  <Input
                    data-testid="input-address"
                    value={form.property_address}
                    onChange={set("property_address")}
                    placeholder={t("prijava.fields.propertyAddressPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
                <Field label={t("prijava.fields.city")} error={errors.city}>
                  <Input
                    data-testid="input-city"
                    value={form.city}
                    onChange={set("city")}
                    placeholder={t("prijava.fields.cityPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <Field label={t("prijava.fields.propertyType")} error={errors.property_type}>
                <Select value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="select-type">
                    <SelectValue placeholder={t("prijava.fields.propertyTypePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPE_VALUES.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`option-${v}`}>
                        {t(`prijava.propertyTypes.${v}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("prijava.fields.yearBuilt")}>
                  <Input
                    data-testid="input-year"
                    value={form.year_built}
                    onChange={set("year_built")}
                    placeholder={t("prijava.fields.yearBuiltPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
                <Field label={t("prijava.fields.area")}>
                  <Input
                    data-testid="input-area"
                    value={form.area_m2}
                    onChange={set("area_m2")}
                    placeholder={t("prijava.fields.areaPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("prijava.fields.cadMunicipality")}>
                  <Input
                    data-testid="input-cadastral-municipality"
                    value={form.cadastral_municipality}
                    onChange={set("cadastral_municipality")}
                    placeholder={t("prijava.fields.cadMunicipalityPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
                <Field label={t("prijava.fields.cadParcel")}>
                  <Input
                    data-testid="input-cadastral-parcel"
                    value={form.cadastral_parcel}
                    onChange={set("cadastral_parcel")}
                    placeholder={t("prijava.fields.cadParcelPlaceholder")}
                    className="rounded-sm"
                  />
                </Field>
              </div>
            </FieldSet>

            <FieldSet title={t("prijava.section4")} n="04">
              <Field label={t("prijava.fields.description")} error={errors.description}>
                <Textarea
                  data-testid="input-description"
                  value={form.description}
                  onChange={set("description")}
                  rows={5}
                  placeholder={t("prijava.fields.descriptionPlaceholder")}
                  className="rounded-sm"
                />
              </Field>
            </FieldSet>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-4 border-t border-hair">
              <p className="text-xs text-ink-soft max-w-md leading-relaxed">
                {t("prijava.consent")}
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="prijava-submit"
                className="inline-flex items-center gap-2 bg-ink text-white px-8 py-3.5 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t("prijava.submitting")}</>
                ) : (
                  <>{t("prijava.submit")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} /></>
                )}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

function FieldSet({ title, n, children }) {
  return (
    <div className="border border-hair bg-white p-6 lg:p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div className="overline text-ink-soft">{title}</div>
        <span className="font-display text-3xl text-ink/15 font-bold leading-none">{n}</span>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-ink-soft font-semibold">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
      {error && <p className="text-xs mt-1.5 text-[#D92D20]">{error}</p>}
    </div>
  );
}
